/*
 * Sign-in (option C, chosen by Kenno on 2026-10-04; no Figma frame of its own): calm and centred.
 * | Part       | Values                                                                                     |
 * | Page       | --surface-page; a soft glow of the moving oil colours (.pattern-oil, as the tours' AI core) |
 * |            | 880 × 640 behind the card, opacity .55, blur 100; the arc pattern as a faint halo around   |
 * |            | it (opacity .16, faded out before the page edges)                                          |
 * | Above      | the tenant mark centred (logo at least 24px high, DEMO tag as in the top bar), gap 24      |
 * | Card       | max 400 wide, --surface-raised, radius 16, padding 40 (24 on phones), gap 24, no rules,    |
 * |            | shadow 0 1px 2px rgb(16 16 19/.06), 0 12px 40px -8px rgb(16 16 19/.16)                     |
 * | Title      | SemiBold 600 22/28, tracking -0.3px; "Welcome back." 400 14/20 --text-weak below, gap 6    |
 * | Fields     | kit TextInput, gap 20; "Remember me", a native checkbox in the accent colour                |
 * | Button     | the kit primary Button, full width, 40 high                                                |
 * | Below      | the distribuAI mark, 80 % opacity, 8px further down                                        |
 *
 * Single sign-on comes after the form: a divider "or" (1px --border-default rules, 14/20
 * --text-weak), then the kit Button's secondary look with monochrome provider marks: two or more
 * side by side with the provider's name ("Microsoft"; the accessible name stays "Continue with
 * Microsoft"), one alone at full width with the full label. A real tenant shows the providers its
 * settings list (`theme.ssoProviders`; none, no divider either), each a link to
 * /api/auth/sso/<provider>/start. A sales demo shows both, unavailable (aria-disabled, the
 * disabled look), with "Not available in the demo" as the tooltip and description.
 * One generic error for every failure, so the form doesn't reveal which accounts exist.
 * A second step (2FA, passkey) is not built; it would replace the card's content.
 */
import { useId, useState } from 'react'
import type { CSSProperties, FormEvent, ReactNode } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router'
import { api } from '@/api/client'
import { setCsrfToken } from '@/api/session'
import distribuaiMark from '@/assets/figma/brand/distribuai-mark.svg'
import arcShapes from '@/assets/figma/brand/pattern-mask-shape.svg'
import { Button, TextInput } from '@/kit'
import { cx } from '@/kit/cx'
import { safeReturnTo } from './returnTo'
import { TenantMark } from './shell/TenantMark'
import type { TenantSettings } from './tenant'

interface LoginResponse {
    success: boolean
    data?: { csrf_token?: string }
}

/** Microsoft's four squares, one colour. */
function MicrosoftMark() {
    return (
        <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true" fill="currentColor">
            <rect x="1" y="1" width="6.5" height="6.5" />
            <rect x="8.5" y="1" width="6.5" height="6.5" />
            <rect x="1" y="8.5" width="6.5" height="6.5" />
            <rect x="8.5" y="8.5" width="6.5" height="6.5" />
        </svg>
    )
}

/** Google's "G", one colour (Simple Icons, CC0). */
function GoogleMark() {
    return (
        <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true" fill="currentColor">
            <path d="M12.48 10.92v3.28h7.84c-.24 1.84-.853 3.187-1.787 4.133-1.147 1.147-2.933 2.4-6.053 2.4-4.827 0-8.6-3.893-8.6-8.72s3.773-8.72 8.6-8.72c2.6 0 4.507 1.027 5.907 2.347l2.307-2.307C18.747 1.44 16.133 0 12.48 0 5.867 0 .307 5.387.307 12s5.56 12 12.173 12c3.573 0 6.267-1.173 8.373-3.36 2.16-2.16 2.84-5.213 2.84-7.667 0-.76-.053-1.467-.173-2.053H12.48z" />
        </svg>
    )
}

const PROVIDERS: Record<string, { mark: () => ReactNode; label: string; name: string }> = {
    microsoft: { mark: MicrosoftMark, label: 'login.sso.microsoft', name: 'Microsoft' },
    google: { mark: GoogleMark, label: 'login.sso.google', name: 'Google' },
}

/** The kit Button's secondary look (Figma 119:13838), full width, as a link or an unavailable button. */
const SSO_BUTTON =
    'inline-flex w-full items-center justify-center gap-[8px] overflow-clip rounded-[8px] px-[12px] py-[8px] no-underline ' +
    'bg-[var(--surface-raised)] text-[var(--text-default)] shadow-[var(--shadow-button-secondary)] ' +
    'focus-visible:shadow-[var(--focus-m)] focus-visible:outline-none pointer-coarse:min-h-[var(--size-touch-target)]'

function SingleSignOn({ providers, demo }: { providers: string[]; demo: boolean }) {
    const { t } = useTranslation()
    const hintId = useId()
    // Side by side with the provider's name when there are two or more; one alone says it in full.
    const row = providers.length > 1
    return (
        <div className="flex flex-col gap-[24px]" data-testid="sso">
            <div className="flex items-center gap-[12px]" role="separator" aria-label={t('login.or')}>
                <span aria-hidden className="h-px flex-1 bg-[var(--border-default)]" />
                <span aria-hidden className="text-[14px] leading-[20px] font-normal text-[var(--text-weak)]">
                    {t('login.or')}
                </span>
                <span aria-hidden className="h-px flex-1 bg-[var(--border-default)]" />
            </div>
            <div className={row ? 'grid grid-cols-2 gap-[8px]' : 'flex flex-col gap-[8px]'}>
                {providers.map((provider) => {
                    const { mark: Mark, label, name } = PROVIDERS[provider]!
                    const content = (
                        <>
                            <Mark />
                            <span className="ss03 whitespace-nowrap text-[14px] leading-[20px] font-[550]">{row ? name : t(label)}</span>
                        </>
                    )
                    return demo ? (
                        <button
                            key={provider}
                            type="button"
                            aria-disabled="true"
                            aria-label={t(label)}
                            aria-describedby={hintId}
                            title={t('login.sso.demo')}
                            className={cx(SSO_BUTTON, 'cursor-not-allowed border-0 opacity-50')}
                        >
                            {content}
                        </button>
                    ) : (
                        <a
                            key={provider}
                            href={`/api/auth/sso/${provider}/start`}
                            aria-label={t(label)}
                            className={cx(SSO_BUTTON, 'cursor-pointer hover:shadow-[var(--shadow-button-secondary-hover)]')}
                        >
                            {content}
                        </a>
                    )
                })}
                {demo && (
                    <span id={hintId} className="sr-only">
                        {t('login.sso.demo')}
                    </span>
                )}
            </div>
        </div>
    )
}

/** The moving oil colours (index.css .pattern-oil), as a layer to mask or blur. */
function Oil({ className, style }: { className?: string; style?: CSSProperties }) {
    return (
        <div aria-hidden className={cx('pattern-oil pointer-events-none absolute overflow-clip', className)} style={style}>
            <span className="pattern-oil-a">
                <span />
            </span>
            <span className="pattern-oil-b">
                <span />
            </span>
        </div>
    )
}

/** The arc pattern (Pattern's arcs) as a halo: the oil colours masked by the arcs and a ring that fades out. */
function ArcHalo() {
    const fade = 'radial-gradient(ellipse 46% 58% at 50% 50%, transparent 32%, #000 62%, transparent 100%)'
    const mask: CSSProperties = {
        maskImage: `url("${arcShapes}"), ${fade}`,
        maskSize: '1800px auto, 100% 100%',
        maskPosition: 'center center, 0 0',
        maskRepeat: 'no-repeat, no-repeat',
        maskComposite: 'intersect',
        WebkitMaskImage: `url("${arcShapes}"), ${fade}`,
        WebkitMaskSize: '1800px auto, 100% 100%',
        WebkitMaskPosition: 'center center, 0 0',
        WebkitMaskRepeat: 'no-repeat, no-repeat',
        WebkitMaskComposite: 'source-in',
        opacity: 0.16,
    }
    return <Oil className="inset-0" style={mask} />
}

export function LoginPage({
    tenant,
    logoHeight,
    demoLabel,
    demoHint,
    ssoProviders,
}: {
    tenant: TenantSettings['data']['tenant']
    logoHeight?: number | null
    demoLabel?: string | null
    demoHint?: string | null
    /** The tenant's single sign-on providers (settings `theme.ssoProviders`); a demo shows all, unavailable. */
    ssoProviders?: string[] | null
}) {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const [params] = useSearchParams()
    const queryClient = useQueryClient()
    const [email, setEmail] = useState('')
    const [password, setPassword] = useState('')
    const [remember, setRemember] = useState(false)
    const [pending, setPending] = useState(false)
    const [failed, setFailed] = useState(false)

    const demo = Boolean(demoLabel)
    const providers = demo ? Object.keys(PROVIDERS) : [...new Set(ssoProviders ?? [])].filter((p) => p in PROVIDERS)

    async function onSubmit(event: FormEvent) {
        event.preventDefault()
        setPending(true)
        setFailed(false)
        try {
            const response = await api.post<LoginResponse>('/api/auth/login.php', { email, password, tenant_id: tenant.id, remember_me: remember }, { handleUnauthorized: false })
            if (!response.success) throw new Error('login failed')
            setCsrfToken(response.data?.csrf_token ?? null)
            await queryClient.invalidateQueries({ queryKey: ['session'] })
            navigate(safeReturnTo(params.get('returnTo')), { replace: true })
        } catch {
            setFailed(true)
            setPassword('')
        } finally {
            setPending(false)
        }
    }

    return (
        <div className="relative flex min-h-screen flex-col items-center justify-center overflow-clip bg-[var(--surface-page)] px-[16px] py-[48px] max-sm:py-[24px]">
            <ArcHalo />
            <Oil className="top-1/2 left-1/2 h-[640px] w-[880px] -translate-x-1/2 -translate-y-1/2 rounded-[50%] opacity-[0.55] blur-[100px] max-sm:h-[460px] max-sm:w-[460px]" />
            <div className="relative flex w-full max-w-[400px] flex-col items-center gap-[24px]">
                <TenantMark logoUrl={tenant.logoUrl} name={tenant.name} logoHeight={Math.max(logoHeight ?? 20, 24)} demoLabel={demoLabel} demoHint={demoHint} />
                <form
                    onSubmit={onSubmit}
                    aria-labelledby="login-title"
                    className="flex w-full flex-col gap-[24px] rounded-[16px] bg-[var(--surface-raised)] p-[40px] shadow-[0_1px_2px_0_rgb(16_16_19/0.06),0_12px_40px_-8px_rgb(16_16_19/0.16)] max-sm:p-[24px]"
                >
                    <div className="flex flex-col gap-[6px]">
                        <h1 id="login-title" className="m-0 text-[22px] leading-[28px] font-semibold tracking-[-0.3px] text-[var(--text-default)]">
                            {t('login.title')}
                        </h1>
                        <p className="m-0 text-[14px] leading-[20px] font-normal text-[var(--text-weak)]">{t('login.welcome')}</p>
                    </div>
                    <div className="flex flex-col gap-[20px]">
                        <TextInput label={t('login.email')} type="email" name="email" autoComplete="username" required value={email} onChange={(e) => setEmail(e.target.value)} />
                        <TextInput
                            label={t('login.password')}
                            type="password"
                            name="password"
                            autoComplete="current-password"
                            required
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                        />
                        <label className="flex cursor-pointer items-center gap-[8px] text-[14px] leading-[20px] font-normal text-[var(--text-default)] pointer-coarse:min-h-[var(--size-touch-target)]">
                            <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} className="size-[16px] accent-[var(--accent-default)]" />
                            {t('login.remember')}
                        </label>
                        {failed && (
                            <p role="alert" className="m-0 text-[14px] leading-[20px] font-normal text-[var(--text-danger)]">
                                {t('login.failed')}
                            </p>
                        )}
                    </div>
                    <Button variant="primary" type="submit" disabled={pending} className="min-h-[40px] w-full">
                        {pending ? t('login.signingIn') : t('login.submit')}
                    </Button>
                    {providers.length > 0 && <SingleSignOn providers={providers} demo={demo} />}
                </form>
                <img src={distribuaiMark} alt="distribuAI" width={71.538} height={20.103} className="mt-[8px] block h-[20.103px] w-[71.538px] opacity-80" />
            </div>
        </div>
    )
}
